import { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { TRIAL_DAYS } from '@/config';
import { PLAN_TYPE } from '@/const/plans';
import { fetchPrices } from '@/store/checkout/thunks';
import { showError } from '@/utils';

const fetchPricesPromises = {};

export function getPercentageByResult(result, total) {
	return (result * 100) / total;
}

function calculateMonthlyPrice(plan) {
	const { baseAmount, recurring, recurringDetails, name } = plan;

	if (name === PLAN_TYPE.TRIAL) {
		return baseAmount * (30 / TRIAL_DAYS);
	}

	if (!recurring) {
		return null;
	}

	const { interval, intervalCount } = recurringDetails;

	if (!interval || !intervalCount) {
		return null;
	}

	const start = dayjs();
	const end = start.add(intervalCount, interval);
	const monthsDiff = end.diff(start, 'month', true);

	return baseAmount / monthsDiff;
}

function extractPlansTypes(plans) {
	if (plans.length === 0) {
		return { cheapest: null, mostExpensive: null };
	}

	let cheapestPlan = plans[0];
	let mostExpensivePlan = plans[0];
	let monthlyPlan = null;
	let lowestPrice = calculateMonthlyPrice(cheapestPlan);
	let highestPrice = lowestPrice;

	for (let i = 0; i < plans.length; i++) {
		const currentPlan = plans[i];
		const currentPrice = calculateMonthlyPrice(currentPlan);

		if (currentPrice < lowestPrice) {
			cheapestPlan = currentPlan;
			lowestPrice = currentPrice;
		}
		if (currentPrice > highestPrice) {
			mostExpensivePlan = currentPlan;
			highestPrice = currentPrice;
		}
		if (currentPlan?.name === PLAN_TYPE.MONTHLY && currentPlan.active) {
			monthlyPlan = currentPlan;
		}
	}

	return {
		cheapest: cheapestPlan,
		mostExpensive: mostExpensivePlan,
		monthlyPlan
	};
}

function usePricesParser(prices = []) {
	const { t } = useTranslation();

	return useMemo(() => {
		const filteredPrices = prices.filter(
			price => ![PLAN_TYPE.SERVICE, PLAN_TYPE.STORAGE].includes(price.name)
		);
		const {
			cheapest: bestPlan,
			mostExpensive,
			monthlyPlan
		} = extractPlansTypes(filteredPrices);

		const listPricePerMonth = mostExpensive
			? calculateMonthlyPrice(monthlyPlan)
			: null;

		return {
			bestPlan,
			monthlyPlan,
			plans: filteredPrices.map(item => {
				const pricePerMonth = calculateMonthlyPrice(item);
				const discount =
					Math.floor(
						getPercentageByResult(
							listPricePerMonth - pricePerMonth,
							listPricePerMonth
						)
					) || 0;

				const parsedItem = {
					...item,
					planName: t(`plans.${item.name}.name`),
					periodicity: t(`plans.${item.name}.periodicity`),
					pricePerMonth,
					listPricePerMonth,
					discount
				};

				return parsedItem;
			})
		};
	}, [prices]);
}

export function usePlans(opts = {}) {
	const dispatch = useDispatch();
	const { onlyActives = true } = opts;

	const [prices, setPrices] = useState([]);
	const [loading, setLoading] = useState(true);

	const currency = useSelector(state => state.profile?.currency);

	const getOnlyActives = (plans = []) => {
		return plans.filter(item => item.active);
	};

	const parsedPrices = onlyActives ? getOnlyActives(prices) : prices;

	const { plans, bestPlan, monthlyPlan } = usePricesParser(parsedPrices);

	const country = useSelector(
		state => state.profile?.user?.taxInformation?.country
	);

	const servicePlan = parsedPrices.find(
		item => item.name === PLAN_TYPE.SERVICE && item.active
	);
	const storagePlan = parsedPrices.find(
		item => item.name === PLAN_TYPE.STORAGE && item.active
	);

	const trialPlan = plans.find(
		item => item.name === PLAN_TYPE.TRIAL && item.active
	);

	useEffect(() => {
		(async () => {
			try {
				setLoading(true);

				let promise = fetchPricesPromises[currency];

				if (!promise) {
					promise = dispatch(
						fetchPrices({ country, currency, filterService: false })
					).unwrap();
					fetchPricesPromises[currency] = promise;
				}

				const response = await promise;

				setPrices(response.prices);
			} catch (error) {
				showError(error);
			} finally {
				setLoading(false);
				fetchPricesPromises[currency] = null;
			}
		})();
	}, [currency]);

	return {
		plans,
		bestPlan,
		monthlyPlan,
		loading,
		servicePlan,
		storagePlan,
		trialPlan
	};
}
